/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Drawer, Form, Select, Row, Col, Divider, DatePicker, Spin } from 'antd';
import { useIntl } from 'react-intl';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import styles from '../styles.module.less';
import { SHIFT_CHANGES_TYPES } from './constants';
import EditMarkDrawerSummary from './components/EditMarkDrawerSummary';
import useEditMarkDrawer from './hooks/useEditMarkDrawer';
import { ABSENCE_REASONS, ABSENCE_TYPES } from '../constants';
import { SHIFT_CHANGES_REQUEST_BY } from '@src/containers/App/TimeTracker/constants';
import { DateUtil } from '@src/packages/utils/dates';

const { Option } = Select;
const prefix = 'timeTracker.reports.editMark';

const EditMarkDrawer = ({ isOpen, setIsOpen, onClose }) => {
  const intl = useIntl();
  const {
    form,
    formData,
    handleChange,
    buttonItems,
    handleClose,
    isLoading,
    disabledStartDate,
    disabledEndTime,
    disabledBreakEndTime,
  } = useEditMarkDrawer({
    Form,
    isOpen,
    setIsOpen,
    onClose,
  });

  const hasDailyHoursWarning = React.useMemo(() => {
    const hoursDiff = DateUtil.getTimeDifference(
      formData?.newStartMarking,
      formData?.newEndMarking,
      'hours',
    );
    return typeof hoursDiff === 'number' && hoursDiff > 10;
  }, [formData?.newStartMarking, formData?.newEndMarking]);

  return (
    <Drawer
      title={null}
      width={600}
      open={isOpen}
      closable={false}
      bodyStyle={{ padding: 0 }}
      placement="right"
      className={styles.editMarkDrawer}
    >
      <BoxContainer>
        <BoxContainer content shadow fixed>
          <FilterContainer
            title={<Title.Header value={intl.formatMessage({ id: `${prefix}.title` })} />}
            goBack={handleClose}
            buttonItems={buttonItems}
          />
        </BoxContainer>

        <BoxContainer content>
          {isLoading && (
            <div className={styles.loader}>
              <Spin />
            </div>
          )}
          <Form className={styles.form} form={form} layout="vertical" initialValues={formData}>
            <EditMarkDrawerSummary />
            <div className="gx-mb-4">
              <Row>
                <Col span={24} className="gx-my-2">
                  <Divider orientation="left">
                    <Title.SubTitle
                      value={intl.formatMessage({ id: `${prefix}.correction.title` })}
                    />
                  </Divider>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={intl.formatMessage({
                      id: `${prefix}.correction.requestedBy.label`,
                    })}
                    name="requestedBy"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: `${prefix}.correction.requestedBy.required`,
                        }),
                      },
                    ]}
                  >
                    <Select
                      onChange={value => handleChange('requestedBy', value)}
                      disabled={isLoading}
                    >
                      <Option value={SHIFT_CHANGES_REQUEST_BY.SUPERVISOR}>
                        {intl.formatMessage({
                          id: `${prefix}.correction.requestedBy.employer`,
                        })}
                      </Option>
                      <Option value={SHIFT_CHANGES_REQUEST_BY.EMPLOYEE}>
                        {intl.formatMessage({
                          id: `${prefix}.correction.requestedBy.employee`,
                        })}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={intl.formatMessage({
                      id: `${prefix}.correction.reason.label`,
                    })}
                    name="correctionReason"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: `${prefix}.correction.reason.required`,
                        }),
                      },
                    ]}
                  >
                    <Select
                      onChange={value => handleChange('correctionReason', value)}
                      disabled={isLoading}
                    >
                      <Option value={SHIFT_CHANGES_TYPES.TIME_ADJUSTMENT}>
                        {intl.formatMessage({
                          id: `${prefix}.correction.reason.markingCorrection`,
                        })}
                      </Option>
                      <Option value={SHIFT_CHANGES_TYPES.LEAVE}>
                        {intl.formatMessage({
                          id: `${prefix}.correction.reason.absence`,
                        })}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                {formData.correctionReason === SHIFT_CHANGES_TYPES.TIME_ADJUSTMENT && (
                  <>
                    <Col span={24} className="gx-my-2">
                      <Divider orientation="left">
                        <Title.SubTitle
                          value={intl.formatMessage({
                            id: `${prefix}.correction.workdayModification.title`,
                          })}
                        />
                      </Divider>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.newStartMarking.label`,
                        })}
                        name="newStartMarking"
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: `${prefix}.correction.newStartMarking.required`,
                            }),
                          },
                        ]}
                      >
                        <DatePicker
                          disabled={isLoading}
                          className="gx-w-100"
                          onChange={value => handleChange('newStartMarking', value)}
                          showTime
                          value={formData.newStartMarking || formData.date}
                          disabledDate={current =>
                            disabledStartDate(current, formData.newStartMarking)
                          }
                          format={`${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.newEndMarking.label`,
                        })}
                        name="newEndMarking"
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: `${prefix}.correction.newEndMarking.required`,
                            }),
                          },
                        ]}
                        className="gx-mb-0"
                      >
                        <DatePicker
                          disabled={isLoading}
                          className="gx-w-100"
                          onChange={value => handleChange('newEndMarking', value)}
                          showTime
                          placement="topLeft"
                          value={formData.newEndMarking}
                          disabledDate={current =>
                            disabledStartDate(current, formData.newStartMarking)
                          }
                          disabledTime={disabledEndTime}
                          format={`${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`}
                        />
                      </Form.Item>

                      {hasDailyHoursWarning && (
                        <div className="ant-form-item-explain-warning gx-mb-2 gx-font-weight-medium">
                          {intl.formatMessage({
                            id: `${prefix}.correction.workdayModification.maxDailyHours.warning`,
                          })}
                        </div>
                      )}
                    </Col>
                    <Col span={24} className="gx-my-2">
                      <Divider orientation="left">
                        <Title.SubTitle
                          value={intl.formatMessage({
                            id: `${prefix}.correction.breakModification.title`,
                          })}
                        />
                      </Divider>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.newBreakStartMarking.label`,
                        })}
                        name="newBreakStartMarking"
                      >
                        <DatePicker
                          disabled={isLoading}
                          className="gx-w-100"
                          onChange={value => handleChange('newBreakStartMarking', value)}
                          showTime
                          placement="topLeft"
                          value={formData.newBreakStartMarking}
                          disabledDate={current =>
                            disabledStartDate(current, formData.newBreakStartMarking)
                          }
                          disabledTime={current => disabledBreakEndTime(current, 'start')}
                          format={`${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.newBreakEndMarking.label`,
                        })}
                        name="newBreakEndMarking"
                      >
                        <DatePicker
                          disabled={isLoading}
                          className="gx-w-100"
                          onChange={value => handleChange('newBreakEndMarking', value)}
                          showTime
                          placement="topLeft"
                          value={formData.newBreakEndMarking}
                          disabledDate={current =>
                            disabledStartDate(current, formData.newBreakEndMarking)
                          }
                          disabledTime={current => disabledBreakEndTime(current, 'end')}
                          format={`${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

                {formData.correctionReason === SHIFT_CHANGES_TYPES.LEAVE && (
                  <>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.isExcused.label`,
                        })}
                        name="isExcused"
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: `${prefix}.correction.isExcused.required`,
                            }),
                          },
                        ]}
                      >
                        <Select
                          onChange={value => handleChange('isExcused', value)}
                          disabled={isLoading}
                        >
                          {ABSENCE_REASONS.map(option => (
                            <Option key={option.label} value={option.code}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: `${prefix}.correction.absenceReason.label`,
                        })}
                        name="absenceReason"
                      >
                        <Select
                          onChange={value => handleChange('absenceReason', value)}
                          disabled={isLoading}
                          showSearch
                          optionFilterProp="children"
                        >
                          {ABSENCE_TYPES.map(option => (
                            <Option key={option.label} value={option.code}>
                              {option.label} ({option.short})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
            </div>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default EditMarkDrawer;
